import { ConsentStepName } from 'src/api/Consent';
import { Vertical } from './Verticals';
import { ErrorProps } from 'app/error';
import { env } from 'src/env.mjs';

export const moshyAuBaseUrl =
    process.env.NODE_ENV === 'development'
        ? 'http://localhost:3002'
        : env.NEXT_PUBLIC_VARIANT === 'staging'
          ? 'https://www.stg.getmoshy.com.au'
          : 'https://www.getmoshy.com.au';

export const moshyNzBaseUrl =
    process.env.NODE_ENV === 'development'
        ? 'http://localhost:3002'
        : env.NEXT_PUBLIC_VARIANT === 'staging'
          ? 'https://www.stg.moshy.co.nz'
          : 'https://www.moshy.co.nz';

const Routes = {
    HOME: `/`,
    ERROR: (params?: ErrorProps['searchParams']) => {
        if (!params || Object.keys(params).length === 0) return '/error';

        const query = new URLSearchParams();

        if (params.error_type) query.append('error_type', params.error_type);
        if (params.error_title) query.append('error_title', params.error_title);
        if (params.error_message)
            query.append('error_message', params.error_message);

        return `/error?${query.toString()}`;
    },
    NOT_FOUND: '/not-found',
    USER_SIGN_IN: (redirectUrl?: string) =>
        redirectUrl
            ? `/login?redirect_url=${encodeURIComponent(redirectUrl)}`
            : `/login`,
    LOGIN: `/login`,
    RESET_PASSWORD: '/reset-password',
    RESET_PASSWORD_SUCCESS: '/reset-password/success',
    RESET_PASSWORD_EXPIRED: '/reset-password/link-expired',
    CREATE_ACCOUNT: `/create-account`,
    CREATE_PASSWORD: '/create-password',
    WELCOME: `/create-account/welcome`,
    VERIFY_EMAIL: (email: string) =>
        `/verify-email?email=${encodeURIComponent(email)}`,
    VERIFY_PHONE: (phoneNumber: string, quizType: string, quizToken: string) =>
        `/verify-phone?phone_number=${encodeURIComponent(phoneNumber)}&quiz_type=${encodeURIComponent(quizType)}&quiz_token=${encodeURIComponent(quizToken)}`,

    INITIAL_QUIZ: (vertical: Vertical) =>
        `/initial/${vertical.toLowerCase().replace(' ', '-')}`,

    HAIR_LOSS_INITIAL_QUIZ: `/initial/hair`,
    HAIR_LOSS_RENEWAL_QUIZ: `/renewal/hair`,
    WEIGHT_LOSS_INITIAL_QUIZ: `/initial/weight_loss`,
    WEIGHT_LOSS_RENEWAL_QUIZ: `/renewal/weight_loss`,
    SKIN_INITIAL_QUIZ: `/initial/skin`,
    SKIN_RENEWAL_QUIZ: `/renewal/skin`,
    SEX_INITIAL_QUIZ: `/initial/sex`,
    SEX_RENEWAL_QUIZ: `/renewal/sex`,
    MENTAL_HEALTH_RENEWAL_QUIZ: '/renewal/mental_health',
    PROSTATE_INITIAL_QUIZ: `/initial/prostate`,
    PROSTATE_SYMPTOMS_QUIZ: `/symptoms/prostate`,
    DIABETES_LANDING_PAGE: '/diabetes',
    DIABETES_INITIAL_QUIZ: `/initial/diabetes`,
    DIABETES_RENEWAL_QUIZ: `/renewal/diabetes`,
    DIABETES_BOOKING: (quizToken: string) =>
        `/quiz_consult?quiz_token=${quizToken}`,
    DIETITIAN_BOOKING: '/booking/dietitian',

    QUIZZES: `/quizzes`,
    QUIZ_CONSULT: `/quiz_consult`,
    QUIZ_CONSULT_BOOKING: `/quiz_consult/booking`,
    MEDICAL_SUPPORT: `/medical-support`,
    MEDICAL_SUPPORT_TICKET: (ticketToken: string) =>
        `/medical-support?ticket=${ticketToken}`,
    MEDICAL_SUPPORT_MEDICATION: (medicationId: string) =>
        `/medical-support/medications/${medicationId}`,
    MEDICAL_SUPPORT_MEDICATION_STEP: (medicationId: string, stepId: number) =>
        `/medical-support/medications/${medicationId}/step/${stepId}`,
    MEDICAL_SUPPORT_MEDICATION_ARTICLES: (medicationId: string) =>
        `/medical-support/medications/${medicationId}/articles`,
    MEDICAL_SUPPORT_MEDICATION_OVERVIEW: (medicationId: string) =>
        `/medical-support/medications/${medicationId}/overview`,
    PRIVACY_POLICY: `/privacy`,
    TERMS_OF_USE: `/terms`,
    PLAN_PAGE: (quizToken: string, vertical: Vertical) =>
        `/review-plan/${vertical.toLowerCase().replace(' ', '-')}?quiz_token=${quizToken}` as `/${string}`,
    RENEWAL_PLAN_CONSENT: (
        quizToken: string,
        vertical: Vertical,
        from: string,
    ) =>
        `/renewal/${vertical.toLowerCase().replace(' ', '-')}/consent?quiz_token=${quizToken}&from=${from}` as `/${string}`,
    RENEWAL_PLAN_CHANGE: (quizToken: string, vertical: Vertical) =>
        `/renewal/${vertical.toLowerCase().replace(' ', '-')}/plan-change?quiz_token=${quizToken}` as `/${string}`,
    RENEWAL_PLAN_CONFIRM: (quizToken: string, vertical: Vertical) =>
        `/renewal/${vertical.toLowerCase().replace(' ', '-')}/plan-confirm?quiz_token=${quizToken}&from=plan_change` as `/${string}`,
    RENEWAL_THANK_YOU: (vertical: Vertical, from: string) =>
        `/renewal/${vertical.toLowerCase().replace(' ', '-')}/thank-you?from=${from}`,
    INITIAL_PLAN_CONSENT: (
        quizToken: string,
        type: Vertical,
        step: ConsentStepName,
    ) =>
        `/initial/${type.toLowerCase().replace(' ', '-')}/consent/${step}?quiz_token=${quizToken}` as const,

    //Checkout
    CHECKOUT: ({
        vertical,
        quiz_token,
        plan_ids,
    }: {
        vertical?: Vertical;
        quiz_token?: string;
        plan_ids?: string[];
    }) => {
        const pathname = vertical
            ? `/checkout/${vertical.toLowerCase().replace(' ', '-')}`
            : '/checkout';

        const params = new URLSearchParams();

        if (quiz_token) {
            params.set('quiz_token', quiz_token);
        }

        if (plan_ids?.length) {
            plan_ids.forEach((id) => params.append('plan_ids[]', id));
        }

        const query = params.toString();
        return (query ? `${pathname}?${query}` : pathname) as `/${string}`;
    },
    THANK_YOU_PAGE_PREPAYMENT: ({ vertical }: { vertical: Vertical }) =>
        `/thank-you/${vertical.toLowerCase().replace(' ', '-')}?prepayment=true` as `/${string}`,
    THANK_YOU_PAGE: ({
        vertical,
        planId,
        quizToken,
    }: {
        vertical: Vertical;
        planId: string;
        quizToken: string;
    }) =>
        `/thank-you/${vertical.toLowerCase().replace(' ', '-')}?plan=${planId}&quiz_token=${quizToken}` as `/${string}`,

    // External
    QIP: 'https://www.qip.com.au/for-consumers/',
} as const;

export default Routes;
